import { useMemo, useState, useEffect, useCallback } from 'react';
import { ref, get, update, onValue, off } from 'firebase/database';
import { database } from '../../firebase/config';
import { getAttendancePath } from '../../helpers/attendanceHelpers';
import { useAppDataContext } from '../../context/AppDataContext';
import { useProducts } from '../../context/ProductsContext';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/index.jsx';
import Icon from '../common/Icon';
import TodayTasks from './TodayTasks';
import AIAssistant from './AIAssistant';

export default function AdminDashboard({ onQuickAction, onOpenIntake }) {
  const { orders, customers, purchases, purchaseOrders, logs, shipments, memos } = useAppDataContext();
  const { bjProducts, marucoProducts } = useProducts();
  const { cartCount } = useCart();
  const { user } = useAuth();
  const { t, language } = useTranslation();
  const products = useMemo(() => [...bjProducts, ...marucoProducts], [bjProducts, marucoProducts]);
  const [resolvingIds, setResolvingIds] = useState(new Set());

  // ── Direct Firebase write for alert resolution ──
  // Does NOT touch memos state (avoids triggering SyncContext dirty → full overwrite).
  // Uses resolvingIds for instant UI feedback; listener auto-refreshes after Firebase write.
  const handleResolveAlert = useCallback(async (memo) => {
    // 1. Mark as resolving (instant UI feedback without touching memos state)
    setResolvingIds(prev => new Set(prev).add(memo.id));

    try {
      // 2. Read /memos from Firebase to find this memo's actual key
      const memosRef = ref(database, 'memos');
      const snapshot = await get(memosRef);
      if (!snapshot.exists()) {
        setResolvingIds(prev => { const s = new Set(prev); s.delete(memo.id); return s; });
        return;
      }

      const data = snapshot.val();
      let targetKey = null;

      // Find the Firebase key (could be numeric index or named key)
      Object.entries(data).forEach(([key, val]) => {
        if (val && val.id === memo.id) {
          targetKey = key;
        }
      });

      if (targetKey === null) {
        console.warn('⚠️ Memo not found in Firebase:', memo.id);
        setResolvingIds(prev => { const s = new Set(prev); s.delete(memo.id); return s; });
        return;
      }

      // 3. Direct per-path write — only update the fields we need
      //    NO setMemos here: listener will pick up the change and update state
      const updates = {};
      updates[`memos/${targetKey}/type`] = 'resolved';
      updates[`memos/${targetKey}/resolvedAt`] = new Date().toISOString();
      updates[`memos/${targetKey}/resolvedBy`] = 'U001';
      await update(ref(database, '/'), updates);

      console.log('✅ Alert resolved in Firebase:', memo.id, 'key:', targetKey);
      // resolvingIds cleanup: listener will update memos → re-render → alert disappears from list
    } catch (err) {
      console.error('Failed to resolve alert in Firebase:', err);
      // Remove from resolving on failure so button becomes clickable again
      setResolvingIds(prev => { const s = new Set(prev); s.delete(memo.id); return s; });
    }
  }, []);

  const totalStock = products.reduce((s, p) => s + (p.stock || 0), 0);
  const totalValue = products.reduce((s, p) => s + (p.stock || 0) * (p.price || 0), 0);

  const today = new Date();
  const dayNames = t('dayNamesWithSun');
  const thisMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const monthStart = `${thisMonth}-01`;

  const pendingOrders = orders.filter(o => o.status === 'pending').length;
  const { lowStockCount, criticalSkuCount, warningSkuCount } = useMemo(() => {
    const skuTotals = {};
    products.forEach(p => {
      if (!p.sku) return;
      skuTotals[p.sku] = (skuTotals[p.sku] || 0) + (p.stock || 0);
    });
    let critical = 0, warning = 0;
    Object.values(skuTotals).forEach(stock => {
      if (stock <= 1) critical++;
      else if (stock === 2) warning++;
    });
    return { lowStockCount: critical + warning, criticalSkuCount: critical, warningSkuCount: warning };
  }, [products]);
  const pendingShipmentCount = (shipments || []).filter(s => s.status === 'pending').length;

  // Monthly sales
  const monthOrders = useMemo(() => {
    return (orders || []).filter(o => o.date >= monthStart && o.status === 'completed');
  }, [orders, monthStart]);
  const monthSales = monthOrders.reduce((s, o) => s + (o.total || 0), 0);

  // Gross margin
  const grossProfit = useMemo(() => {
    let profit = 0;
    monthOrders.forEach(o => {
      (o.items || []).forEach(item => {
        const product = products.find(p => p.id === item.id);
        const cost = item.cost || product?.cost || Math.floor((item.price || 0) * 0.6);
        profit += ((item.price || 0) - cost) * (item.qty || 0);
      });
    });
    return profit;
  }, [monthOrders, products]);
  const grossMargin = monthSales > 0 ? ((grossProfit / monthSales) * 100).toFixed(1) : 0;

  // Monthly purchases — from LOT (stock_receipt) data in Firebase
  const [intakeLots, setIntakeLots] = useState([]);
  useEffect(() => {
    const lotRef = ref(database, 'stock_receipt');
    const cb = (snap) => {
      if (!snap.exists()) { setIntakeLots([]); return; }
      const data = snap.val();
      setIntakeLots(Object.values(data).filter(v => v && typeof v === 'object'));
    };
    onValue(lotRef, cb);
    return () => off(lotRef, 'value', cb);
  }, []);

  const monthPurchaseTotal = useMemo(() => {
    return intakeLots
      .filter(lot => {
        if (lot.status === 'cancelled') return false;
        // Match by date field
        if (lot.date && lot.date.startsWith(thisMonth)) return true;
        // Match by lot_number LOT-YYMMDD pattern
        const lotNum = lot.lot_number || lot.lot_id || '';
        const m = lotNum.match(/^LOT-(\d{2})(\d{2})(\d{2})/);
        if (m) {
          const lotMonth = `20${m[1]}-${m[2]}`;
          if (lotMonth === thisMonth) return true;
        }
        // Match by created_at
        if (lot.created_at && lot.created_at.startsWith(thisMonth)) return true;
        return false;
      })
      .reduce((sum, lot) => {
        // Use total_cost if available, else sum items cost_price
        if (lot.total_cost > 0) return sum + lot.total_cost;
        return sum + (lot.items || []).reduce((s, item) => {
          const qty = item.qty || item.quantity || 1;
          return s + (item.cost_price || 0) * qty;
        }, 0);
      }, 0);
  }, [intakeLots, thisMonth]);

  // PO alerts
  const unconfirmedPOs = useMemo(() => {
    const poArr = Array.isArray(purchaseOrders) ? purchaseOrders : [];
    return poArr.filter(po => po.status === 'received').length;
  }, [purchaseOrders]);
  const unpaidPOs = useMemo(() => {
    const poArr = Array.isArray(purchaseOrders) ? purchaseOrders : [];
    return poArr.filter(po => po.status === 'confirmed').length;
  }, [purchaseOrders]);

  // Attendance rate — direct Firebase listener
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  useEffect(() => {
    const attendRef = ref(database, getAttendancePath(thisMonth));
    const cb = (snap) => {
      if (!snap.exists()) { setAttendanceRecords([]); return; }
      const data = snap.val();
      const records = [];
      Object.entries(data).forEach(([userId, dates]) => {
        if (dates && typeof dates === 'object') {
          Object.values(dates).forEach(record => {
            if (record && record.date) records.push(record);
          });
        }
      });
      setAttendanceRecords(records);
    };
    onValue(attendRef, cb);
    return () => off(attendRef, 'value', cb);
  }, [thisMonth]);

  const attendanceRate = useMemo(() => {
    const workDays = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    const actualDays = new Set(attendanceRecords.map(a => a.date)).size;
    return workDays > 0 ? Math.round((actualDays / Math.min(today.getDate(), workDays)) * 100) : 0;
  }, [attendanceRecords, today]);

  // Weekly sales (Sunday-first: index 0=Sun, 1=Mon, ..., 6=Sat)
  const weekSales = useMemo(() => {
    const result = [0, 0, 0, 0, 0, 0, 0];
    const dayOfWeek = today.getDay(); // 0=Sun
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - dayOfWeek);
    sunday.setHours(0, 0, 0, 0);
    const sundayStr = `${sunday.getFullYear()}-${String(sunday.getMonth() + 1).padStart(2, '0')}-${String(sunday.getDate()).padStart(2, '0')}`;
    (orders || []).filter(o => o.date >= sundayStr && o.status === 'completed').forEach(o => {
      const oDate = new Date(o.date + 'T12:00:00');
      const oDay = oDate.getDay(); // 0=Sun
      result[oDay] += o.total || 0;
    });
    return result;
  }, [orders, today]);

  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  const todayOrders = (orders || []).filter(o => o.date === todayStr && o.status === 'completed');
  const todaySales = todayOrders.reduce((s, o) => s + (o.total || 0), 0);

  return (
    <div className="space-y-4 animate-fadeIn">
      {/* Greeting */}
      <div>
        <div className="text-t-primary text-xl font-bold">
          {t('dashboard.greeting', { name: user?.name?.split(' ')?.[0] || user?.name || '' })}
        </div>
        <div className="text-t-secondary text-sm mt-0.5">
          {today.toLocaleDateString(language === 'en' ? 'en-US' : 'ja-JP', { month: 'long', day: 'numeric' })} ({dayNames[today.getDay()]})
        </div>
      </div>

      {/* Hero KPI Cards */}
      <div className="flex gap-3 overflow-x-auto hide-scrollbar -mx-4 px-4">
        {/* Total Inventory */}
        <div className="min-w-[180px] flex-shrink-0 bg-t-card rounded-2xl border border-t-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
              <Icon name="Wine" size={16} className="text-emerald-400" />
            </div>
            <span className="text-t-secondary text-xs">{t('dashboard.totalInventory')}</span>
          </div>
          <div className="text-t-primary text-2xl font-bold">{totalStock}<span className="text-sm text-t-secondary ml-1">{t('dashboard.bottles')}</span></div>
          <div className="text-t-tertiary text-xs mt-1">¥{(totalValue / 10000).toFixed(0)}{t('dashboard.tenThousandUnit')}</div>
        </div>

        {/* Today's Sales */}
        <div className="min-w-[180px] flex-shrink-0 bg-t-card rounded-2xl border border-t-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center">
              <Icon name="TrendingUp" size={16} className="text-blue-400" />
            </div>
            <span className="text-t-secondary text-xs">{t('dashboard.todaySales')}</span>
          </div>
          <div className="text-t-primary text-2xl font-bold">¥{todaySales.toLocaleString()}</div>
          <div className="text-green-400 text-xs mt-1">↑ {todayOrders.length}{t('dashboard.items')}</div>
        </div>

        {/* Monthly Revenue */}
        <div className="min-w-[180px] flex-shrink-0 bg-t-card rounded-2xl border border-t-border p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-purple-500/15 flex items-center justify-center">
              <Icon name="BarChart3" size={16} className="text-purple-400" />
            </div>
            <span className="text-t-secondary text-xs">{t('dashboard.monthlyRevenue')}</span>
          </div>
          <div className="text-t-primary text-2xl font-bold">¥{(monthSales / 10000).toFixed(0)}<span className="text-sm text-t-secondary">{t('dashboard.tenThousandUnit')}</span></div>
          <div className={`text-xs mt-1 ${parseFloat(grossMargin) >= 30 ? 'text-green-400' : parseFloat(grossMargin) >= 15 ? 'text-yellow-400' : 'text-red-400'}`}>{t('kpi.grossMargin')}: {grossMargin}%</div>
        </div>

        {/* Monthly Purchases — tap to navigate to purchase page */}
        <div className="min-w-[180px] flex-shrink-0 bg-t-card rounded-2xl border border-t-border p-4 cursor-pointer active:scale-[0.98] transition-transform" onClick={() => onQuickAction('purchases')}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-orange-500/15 flex items-center justify-center">
              <Icon name="PackagePlus" size={16} className="text-orange-400" />
            </div>
            <span className="text-t-secondary text-xs">{t('dashboard.monthlyPurchases', '今月仕入')}</span>
          </div>
          <div className="text-t-primary text-2xl font-bold">¥{(monthPurchaseTotal / 10000).toFixed(0)}<span className="text-sm text-t-secondary">{t('dashboard.tenThousandUnit')}</span></div>
          <div className="text-t-tertiary text-xs mt-1">{thisMonth}</div>
        </div>
      </div>

      {/* Quick Actions — Circular */}
      <div className="flex justify-around py-2">
        {[
          { icon: 'PackagePlus', label: t('dashboard.stockIn'), action: 'addStock', bg: 'bg-emerald-500/15', color: 'text-emerald-400' },
          { icon: 'ShoppingBag', label: t('dashboard.sales'), action: 'newSale', bg: 'bg-blue-500/15', color: 'text-blue-400', badge: cartCount },
          { icon: 'Users', label: t('nav.customers'), action: 'customers', bg: 'bg-purple-500/15', color: 'text-purple-400' },
          { icon: 'Package', label: t('nav.inventory'), action: 'inventory', bg: 'bg-amber-500/15', color: 'text-amber-400' },
        ].map(item => (
          <button key={item.action} onClick={() => onQuickAction(item.action)} className="flex flex-col items-center gap-1.5 active:scale-95 transition-transform relative">
            <div className={`w-14 h-14 rounded-full ${item.bg} flex items-center justify-center`}>
              <Icon name={item.icon} size={24} className={item.color} />
            </div>
            <span className="text-t-secondary text-[10px] font-medium">{item.label}</span>
            {item.badge > 0 && (
              <span className="absolute -top-0.5 right-0 bg-red-500 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold">{item.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* Action Required */}
      {(pendingOrders > 0 || lowStockCount > 0 || pendingShipmentCount > 0 || unconfirmedPOs > 0 || unpaidPOs > 0) && (
        <div className="bg-t-card rounded-2xl border border-t-border overflow-hidden">
          <div className="px-4 py-3 border-b border-t-border flex items-center">
            <Icon name="AlertCircle" size={16} className="text-yellow-500 mr-2" />
            <span className="text-t-primary font-medium text-sm">{t('dashboard.actionRequired')}</span>
          </div>
          <div className="divide-y divide-t-border">
            {pendingOrders > 0 && (
              <button onClick={() => onQuickAction('orders')} className="w-full px-4 py-3 flex items-center justify-between hover:bg-t-card-hover transition">
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></span>
                  <span className="text-t-primary text-sm">{t('dashboard.ordersPending', { count: pendingOrders })}</span>
                </div>
                <Icon name="ChevronRight" size={16} className="text-t-secondary" />
              </button>
            )}
            {lowStockCount > 0 && (
              <button onClick={() => onQuickAction('lowstock')} className="w-full px-4 py-3 flex items-center justify-between hover:bg-t-card-hover transition">
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                  <span className="text-t-primary text-sm">{t('dashboard.lowStock', { count: lowStockCount })}</span>
                </div>
                <Icon name="ChevronRight" size={16} className="text-t-secondary" />
              </button>
            )}
            {pendingShipmentCount > 0 && (
              <button onClick={() => onQuickAction('orders')} className="w-full px-4 py-3 flex items-center justify-between hover:bg-t-card-hover transition">
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full mr-3"></span>
                  <span className="text-t-primary text-sm">{t('shipment.pendingCount', { count: pendingShipmentCount })}</span>
                </div>
                <Icon name="ChevronRight" size={16} className="text-t-secondary" />
              </button>
            )}
            {unconfirmedPOs > 0 && (
              <div className="w-full px-4 py-3 flex items-center justify-between">
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-purple-500 rounded-full mr-3"></span>
                  <span className="text-t-primary text-sm">{language === 'en' ? `${unconfirmedPOs} unconfirmed POs` : `${unconfirmedPOs}件 未確認入荷`}</span>
                </div>
                <Icon name="PackageCheck" size={16} className="text-purple-400" />
              </div>
            )}
            {unpaidPOs > 0 && (
              <div className="w-full px-4 py-3 flex items-center justify-between">
                <div className="flex items-center">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
                  <span className="text-t-primary text-sm">{language === 'en' ? `${unpaidPOs} unpaid POs` : `${unpaidPOs}件 未払い発注`}</span>
                </div>
                <Icon name="CreditCard" size={16} className="text-orange-400" />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stock Check Alerts */}
      {(() => {
        const alertMemos = (memos || []).filter(m => m.type === 'alert' && !resolvingIds.has(m.id) && (m.target === 'admin' || m.targetId === 'U001'));
        if (alertMemos.length === 0) return null;
        return (
          <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-2xl overflow-hidden">
            <div className="px-4 py-3 border-b border-yellow-500/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="AlertTriangle" size={16} className="text-yellow-500" />
                <span className="text-yellow-400 font-medium text-sm">{t('dashboard.stockAlerts')}</span>
              </div>
              <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded-full text-xs font-bold">{alertMemos.length}</span>
            </div>
            <div className="divide-y divide-yellow-500/10 max-h-48 overflow-y-auto">
              {alertMemos.slice(0, 10).map(m => (
                <div key={m.id} className="px-4 py-2.5 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-t-primary text-sm">{m.text}</div>
                    <div className="text-t-secondary text-xs mt-0.5">{m.author} · {new Date(m.createdAt).toLocaleDateString('ja-JP')}</div>
                  </div>
                  <button
                    onClick={() => handleResolveAlert(m)}
                    className="flex-shrink-0 px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs"
                  >
                    {t('dashboard.alertResolve')}
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })()}


      {/* Today's Tasks */}
      <TodayTasks />

      {/* AI Assistant */}
      <AIAssistant onOpenIntake={onOpenIntake} />

      {/* Recent Activity */}
      {(() => {
        const timeAgo = (dateStr) => {
          if (!dateStr) return '';
          const diffMin = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
          if (diffMin < 1) return language === 'en' ? 'just now' : 'たった今';
          if (diffMin < 60) return language === 'en' ? `${diffMin}m ago` : `${diffMin}分前`;
          const diffH = Math.floor(diffMin / 60);
          if (diffH < 24) return language === 'en' ? `${diffH}h ago` : `${diffH}時間前`;
          const diffD = Math.floor(diffH / 24);
          return language === 'en' ? `${diffD}d ago` : `${diffD}日前`;
        };

        const items = [];
        const orderArr = Array.isArray(orders) ? orders : Object.values(orders || {});
        orderArr.forEach(o => {
          if (!o || !o.date) return;
          const itemsSummary = (o.items || []).slice(0, 2).map(i => i.name || i.sku || '商品').join(', ');
          const badge = o.status === 'completed' ? { text: t('activity.sold'), color: 'blue' }
            : o.status === 'paid' ? { text: t('activity.paid'), color: 'green' }
            : { text: t('activity.processing'), color: 'yellow' };
          items.push({
            title: itemsSummary || `¥${(o.total || 0).toLocaleString()}`,
            sub: `${timeAgo(o.date)} · ¥${(o.total || 0).toLocaleString()}`,
            badge, sortDate: o.date, icon: 'ShoppingBag',
          });
        });
        const logArr = Array.isArray(logs) ? logs : Object.values(logs || {});
        logArr.forEach(l => {
          if (!l || !l.date) return;
          const badge = l.type === 'intake' ? { text: t('activity.intake'), color: 'green' }
            : l.type === 'stock_check' ? { text: t('activity.checked'), color: 'green' }
            : l.type === 'alert' ? { text: t('activity.alert'), color: 'red' }
            : { text: t('activity.log'), color: 'gray' };
          items.push({
            title: l.action || l.text || l.type || '',
            sub: `${timeAgo(l.date)} · ${l.user || l.author || ''}`,
            badge, sortDate: l.date, icon: l.type === 'intake' ? 'PackagePlus' : l.type === 'alert' ? 'AlertTriangle' : 'ClipboardCheck',
          });
        });
        items.sort((a, b) => (b.sortDate || '').localeCompare(a.sortDate || ''));
        const recent = items.slice(0, 5);

        if (recent.length === 0) return null;

        const badgeColors = {
          blue: 'bg-blue-500/20 text-blue-400',
          green: 'bg-green-500/20 text-green-400',
          yellow: 'bg-yellow-500/20 text-yellow-400',
          red: 'bg-red-500/20 text-red-400',
          gray: 'bg-gray-500/20 text-gray-400',
        };

        return (
          <div className="bg-t-card rounded-2xl border border-t-border overflow-hidden">
            <div className="px-4 py-3 border-b border-t-border flex items-center gap-2">
              <Icon name="Activity" size={16} className="text-t-tertiary" />
              <span className="text-t-primary font-medium text-sm">{t('dashboard.recentActivity')}</span>
            </div>
            <div className="divide-y divide-t-border">
              {recent.map((item, i) => (
                <div key={i} className="px-4 py-2.5 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-t-surface flex items-center justify-center flex-shrink-0">
                    <Icon name={item.icon} size={16} className="text-t-tertiary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-t-primary text-sm truncate">{item.title}</div>
                    <div className="text-t-secondary text-xs">{item.sub}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${badgeColors[item.badge.color]}`}>
                    {item.badge.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Weekly Sales Chart */}
      <div className="bg-t-card rounded-2xl border border-t-border p-4">
        <div className="text-t-primary font-medium text-sm mb-4">{t('dashboard.weeklySales')}</div>
        <div className="flex items-end justify-between gap-1" style={{height: '120px'}}>
          {weekSales.map((sale, i) => {
            const maxSale = Math.max(...weekSales);
            const heightPx = sale > 0 ? Math.max(20, (sale / (maxSale || 1)) * 100) : 4;
            const isToday = i === today.getDay();
            return (
              <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
                <div className={`w-full rounded-t ${isToday ? 'bg-[var(--theme-accent)]' : sale > 0 ? 'bg-green-500' : 'bg-t-surface'}`} style={{height: `${heightPx}px`}}></div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between gap-1 mt-2">
          {weekSales.map((sale, i) => {
            const isToday = i === today.getDay();
            return (
              <div key={i} className="flex-1 text-center">
                <span className={`text-xs ${isToday ? 'text-t-accent font-bold' : 'text-t-secondary'}`}>{t('dayNamesWithSun')[i]}</span>
                {sale > 0 && <div className="text-xs text-t-tertiary">¥{(sale/10000).toFixed(1)}{t('dashboard.tenThousandUnit')}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
